/**
 * Sesotho: Broadcasts
 *
 * What this file covers: Messages to many contacts at once: audiences, scheduling, costs, delivery reports.
 *
 * Tone: Careful and exact about who receives what and what it costs. Numbers, counts and dates must stay
 * exactly as written.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const broadcasts: Catalog = {
  'broadcasts.title': 'Liphatlalatso',
  'broadcasts.actions.new': 'Phatlalatso e ncha',
  'broadcasts.loadError': 'Ha ea khona ho kenya liphatlalatso.',
  'broadcasts.empty': 'Ha ho liphatlalatso hajoale.',
  'broadcasts.columns.template': 'Template',
  'broadcasts.columns.sentTotal': 'E rometsoe / Kakaretso',
  'broadcasts.form.phoneNumber': 'Nomoro ea mohala',
  'broadcasts.form.noNumbers': 'Ha ho linomoro tse hokeletsoeng',
  'broadcasts.form.templateLanguage': 'Puo ea template',
  'broadcasts.form.recipients': 'Baamoheli ({{count}})',
  'broadcasts.form.phoneNumberRequired': 'Khetha nomoro ea mohala.',
  'broadcasts.form.templateRequired': 'Khetha template.',
  'broadcasts.form.recipientsRequired': 'Eketsa bonyane moamoheli a le mong.',
  'broadcasts.form.schedule': 'Rera phatlalatso',
  'broadcasts.toast.recipientsTitle': 'Baamoheli',
  'broadcasts.toast.createFailed': 'Ho theha ho hlolehile',
  'broadcasts.view.notFound': 'Phatlalatso ha e fumanehe.',
  'broadcasts.view.backToAll': 'Liphatlalatso tsohle',
  'broadcasts.view.tabs.stats': 'Lipalo-palo',
  'broadcasts.view.tabs.lifecycle': 'Potoloho ea bophelo',
  'broadcasts.view.overview.recipientsHint': 'baamoheli',
  'broadcasts.view.overview.filtered': 'E sefiloe',
  'broadcasts.view.overview.progress': 'Tsoelopele',
  'broadcasts.view.overview.timing': 'Nako',
  'broadcasts.view.overview.finished': 'E phethiloe',
  'broadcasts.view.stats.title': 'Karohano ea ho fihlisa',
  'broadcasts.view.lifecycle.createdDraft': 'E thehiloe (moralo)',
  'broadcasts.view.lifecycle.started': 'E qalile',
  'broadcasts.view.lifecycle.live': 'e phelang',
  'broadcasts.view.lifecycle.pending': 'e emetse',
  'broadcasts.view.lifecycle.startNow': 'Qala hona joale',
  'broadcasts.view.lifecycle.cancelBroadcast': 'Hlakola phatlalatso',
  'broadcasts.view.lifecycle.modal.cancel.title': 'Hlakola phatlalatso ena?',
  'broadcasts.view.lifecycle.modal.cancel.keep': 'Tsoela pele',
  'broadcasts.view.lifecycle.toast.started': 'Phatlalatso e qalile.',
  'broadcasts.view.lifecycle.toast.startFailed': 'Ha ea khona ho qala.',
  'broadcasts.view.lifecycle.toast.cancelled': 'Phatlalatso e hlakotsoe.',
  'broadcasts.view.lifecycle.toast.cancelFailed': 'Ha ea khona ho hlakola.',
  'broadcasts.form.audience.tag': 'Letśoao',
  'broadcasts.form.tag': 'Letśoao',
  'broadcasts.form.tag.option': '{{tag}} ({{count}})',
  'broadcasts.form.tagRequired': 'Khetha letśoao leo u batlang ho romela ho lona.',
  'broadcasts.form.tagCount': 'Sena se tla fihla ho batho ba {{count}} ba nang le letśoao {{tag}}.',
  'broadcasts.new.title': 'Phatlalatso e ncha',
  'broadcasts.new.description': 'Khetha hore e ea ho mang, e reng le hore neng. Ha ho letho le romelloang pele ho mohato wa ho qetela.',
  'broadcasts.new.step.audience': 'Bamameli',
  'broadcasts.new.step.message': 'Molaetsa',
  'broadcasts.new.step.when': 'Neng',
  'broadcasts.new.step.review': 'Hlahloba mme o romele',
  'broadcasts.new.action.send': 'Romela hona joale',
  'broadcasts.new.action.schedule': 'E behele nako',
  'broadcasts.new.sent.now': 'E romelloa ho bamameli ba {{count}}.',
  'broadcasts.new.sent.scheduled': 'E beheletsoe bamameli ba {{count}}.',
  'broadcasts.new.previewFailed': 'Bamameli ha ba ka ba baloa hona joale. Dipalo tse ka tlase di ka be di se di tsofetse.',
  'broadcasts.new.draft.saved': 'Mongolo o bolokiloe ka {{time}}',
  'broadcasts.new.draft.saving': 'Mongolo oa bolokoa…',
  'broadcasts.new.draft.unavailable': 'Mongolo ona ha o bolokoe. O qetele tabeng ena.',
  'broadcasts.new.draft.resumed': 'Ho tsoetsoe pele ka mongolo oa hao o bolokiloeng.',
  'broadcasts.new.draft.discard': 'Lahla mongolo',
  'broadcasts.new.draft.discard.title': 'Lahla mongolo ona?',
  'broadcasts.new.draft.discard.body': 'Tsohle tse tlatsitsoeng ho fihlela joale di ea lahleloa. Ha ho letho le romeletsoeng.',
  'broadcasts.new.leave.title': 'O tlohela phatlalatso ena?',
  'broadcasts.new.leave.body': 'Ha e e-so romelloe. Mongolo oa hao o bolokiloe, kahoo o ka tsoela pele moo o emisitseng teng.',
  'broadcasts.new.leave.discard': 'Tsamaea',
  'broadcasts.new.leave.keep': 'Lula mona',
  'broadcasts.new.refusal.recipients_required': 'Khetha hore ena e ea ho mang.',
  'broadcasts.new.refusal.tag_empty': 'Ha ho motho ea nang le letšoao leo ea ntseng a amohela, kahoo sena se ne se ke ke sa fihla ho motho.',
  'broadcasts.new.refusal.segment_empty': 'Karolo eo ha e tsamaellane le motho ea ka romelloang molaetsa.',
  'broadcasts.new.refusal.segment_too_broad': 'Karolo eo ha e na melao, kahoo e tsamaellana le motho e mong le e mong eo o nang le eena. Eketsa molao pele o romela ho eona.',
  'broadcasts.new.refusal.audience_all_opted_out': 'Batho bohle bamameling bana ba kopile ho se amohele melaetsa.',
  'broadcasts.new.audience.numberHint': 'Nomoro eo melaetsa ena e tla tsoa ho eona.',
  'broadcasts.new.audience.noNumbers': 'Ha ho dinomoro tse hokahantsoeng hajoale. Hokahanya e le nngoe pele o romela.',
  'broadcasts.new.audience.noSegments': 'Ha o na dikarolo tse bolokiloeng hajoale. Romela ho letšoao ho e-na le hoo, kapa o boloke karolo leqepheng la Dikamano.',
  'broadcasts.new.audience.willReceive': 'ba tla e amohela',
  'broadcasts.new.audience.breakdown': '{{matched}} bamameling bana, {{excluded}} ba tlositsoe.',
  'broadcasts.new.audience.optedOut': 'Ba {{count}} ho bona ba kopile ho se amohele melaetsa, kahoo ba siuoa. Ha ba baloe mme ha ho lefelloe bona.',
  'broadcasts.new.audience.large.title': 'Bao ke bamameli ba bangata',
  'broadcasts.new.audience.large.body': 'Sena se ea ho batho ba {{count}} mme se ke ke sa khutlisoa hang ha se qalile. Hlahloba bamameli pele o romela.',
  'broadcasts.new.audience.cannotSend': 'Ha ho khonehe ho romela ho bamameli bana',
  'broadcasts.new.audience.counting': 'Bamameli ba ntse ba baloa…',
  'broadcasts.new.message.noneApproved.title': 'Ha ho di-templeite tse amohetsoeng hajoale',
  'broadcasts.new.message.noneApproved.body': 'Phatlalatso e romela templeite eo WhatsApp e e amohetseng, e seng mongolo o lokolohileng. Ngola e le nngoe o e romele hore e amoheloe, ebe oa khutla.',
  'broadcasts.new.message.noneApproved.action': 'Etsa templeite',
  'broadcasts.new.message.noPlaceholders': 'Templeite ena ha e na dikheo, kahoo e romela mantsoe a tšoanang ho bohle.',
  'broadcasts.new.message.previewEmpty': 'Templeite ena ha e na \'mele oa molaetsa.',
  'broadcasts.new.message.previewBusiness': 'Khoebo ea hao',
  'broadcasts.new.message.previewFor': 'Sena ke seo {{who}} a tla se amohela.',
  'broadcasts.new.message.previewNoContact': 'Khetha bamameli ho bona sena ka motho oa \'nete.',
  'broadcasts.new.message.fillWith': 'Tlatsa kheo {{index}} ka',
  'broadcasts.new.message.fill.literal': 'Mantsoe a tšoanang ho bohle',
  'broadcasts.new.message.field.name': 'Lebitso la motho',
  'broadcasts.new.message.field.email': 'Imeile ea motho',
  'broadcasts.new.message.field.locale': 'Puo ea motho',
  'broadcasts.new.message.field.msisdn': 'Nomoro ea motho',
  'broadcasts.new.message.fallback': 'Haeba ba se na eona',
  'broadcasts.new.message.fallbackHint': 'WhatsApp e hana molaetsa o nang le kheo e se nang letho, kahoo sena ke se tla tsamaea ho mang kapa mang ea se nang letho moo.',
  'broadcasts.new.message.personalised': 'Motho e mong le e mong o bona ea hae mona.',
  'broadcasts.new.when.label': 'Sena se lokela ho tsoa neng?',
  'broadcasts.new.when.now': 'Romela hona joale',
  'broadcasts.new.when.nowHint': 'Se qala ho tsoa hang ha o tobetsa Romela.',
  'broadcasts.new.when.later': 'Romela ka nako e behiloeng',
  'broadcasts.new.when.laterHint': 'Se emela nako eo o e khethileng, ebe se iqalela.',
  'broadcasts.new.when.zoneHint': 'E baloa ka nako ea mokhatlo oa hao, {{zone}}.',
  'broadcasts.new.when.goesOut': 'E tsoa {{when}} ({{zone}}).',
  'broadcasts.new.when.yourClock': 'Ka oache ea hao ke {{when}} ({{zone}}).',
  'broadcasts.new.when.differentZone': 'Mokhatlo oa hao o behiloe ho {{org}} athe sesebelisoa sena se ho {{browser}}. Nako e ka holimo ke ea mokhatlo oa hao.',
  'broadcasts.new.when.required': 'Khetha letsatsi le nako, kapa o romele hona joale.',
  'broadcasts.new.when.past': 'Nako eo e se e fetile. Khetha e latelang, kapa o romele hona joale.',
  'broadcasts.new.review.recipients': 'Baamoheli',
  'broadcasts.new.review.recipientsHint': '{{matched}} bamameling, {{excluded}} ba kopileng ho tlohela',
  'broadcasts.new.review.cost': 'Litšenyehelo tse lekanyetsoang',
  'broadcasts.new.review.costHint': 'bakeng sa melaetsa e {{count}}, ka litheko tsa hao tse ngodisitsoeng',
  'broadcasts.new.review.costUnknown': 'ha e fumanehe hajoale',
  'broadcasts.new.review.balance': 'Chelete e setseng sepaching',
  'broadcasts.new.review.balanceHint': 'hoo e ka bang {{after}} e setseng kamora moo',
  'broadcasts.new.review.balanceUnknown': 'ha e fumanehe hajoale',
  'broadcasts.new.review.at': '{{when}} ({{zone}})',
  'broadcasts.new.review.immediately': 'Hang ha o tobetsa Romela',
  'broadcasts.new.review.runsOut.title': 'Haeba sepache se fela bohareng',
  'broadcasts.new.review.runsOut.body': 'Dikrediti di hlahlojoa molaetsa ka mong. Haeba chelete e fela bohareng, batho ba seng ba fihletsoe ba lula ba fihletsoe, ba bang ba emela, mme ha ho letho le leng le tsoang ho fihlela o eketsa.',
  'broadcasts.new.review.topUp': 'Eketsa sepaching sa hao',
  'broadcasts.new.review.noPreview': 'Dipalo tsa phatlalatso ena ha di ka ba baloa. Khutlela morao mohato o le mong o hlahlobe bamameli.',
  'broadcasts.new.leave.body.unsaved': 'Ha e e-so romelloe, mme mongolo ona ha o bolokoe, kahoo ho tsamaea ho lahla tsohle tseo o li tlatsitseng.',
  'broadcasts.view.recipients.attempts': 'maiteko a {{count}}',
  'broadcasts.recipientError.INSUFFICIENT_CREDITS': 'Ho ne ho se na tjhelete e lekaneng sepatjheng ha molaetsa ona o ne o romelwa.',
  'broadcasts.recipientError.PHONE_NOT_FOUND': 'Nomoro e romelang ha e sa sebetsa akhaonteng ena.',
  'broadcasts.recipientError.ACCOUNT_NOT_PAYABLE': 'WhatsApp e e hanne ka lebaka la bothata ba tefo lehlakoreng la rona, e seng la hao.',
  'broadcasts.recipientError.WINDOW_CLOSED': 'WhatsApp e dumella feela molaetsa o tlwaelehileng hara dihora tse 24 kamora karabo ya ho qetela ya motho.',
  'broadcasts.recipientError.UNDELIVERABLE': 'WhatsApp ha ea khona ho fihlisa ho nomoro ena.',
  'broadcasts.recipientError.NUMBER_NOT_REGISTERED': 'Nomoro ena ha e ho WhatsApp.',
  'broadcasts.recipientError.META_REFUSED': 'WhatsApp ha ea amohela molaetsa ona.',
  'broadcasts.recipientError.SEND_FAILED': 'Teko ya pele ya ho romela molaetsa ona e hlolehile.',
  'broadcasts.recipientError.DISPATCH_GAVE_UP': 'Ha ea ka ya fetiswa ho WhatsApp kamora maiteko a mararo. Hlahloba nomoro le sepatjhe, ebe o romela ho motho enwa hape.',
  'broadcasts.recipientError.UNEXPECTED_RESPONSE': 'Tshebeletso ya melaetsa e fane ka karabo eo re sa kgonang ho e bala.',
  'broadcasts.new.message.literalHint': 'Tlanya {{ ho tlatsa sena ho tswa ho mokopani sebakeng sa moo, jwalo ka lebitso la hae.',
  'broadcasts.new.review.placeholder': 'Sebaka sa ho tshwara {{index}}'
};

export default broadcasts;
